import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { AxiosPromise } from 'axios';
import { ServerResponse } from 'data/types'
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as alarmNotificationProfileSelectors from 'data/alarmNotificationProfile/alarmNotificationProfile.selectors';
import { RootState } from 'data/root.reducers';
import * as alarmNotificationProfileActions from 'data/alarmNotificationProfile/alarmNotificationProfile.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as alarmNotificationApi from 'data/alarmNotificationProfile/alarmNotificationProfile.api';
import AlarmNotificationProfileForm from 'routes/AlarmNotificationProfile/components/AlarmNotificationProfileForm';
import {
  AlarmNotificationProfile, AdaptedAlarmNotificationProfile, AlarmsDef, AlarmsInst, InterceptAlarm
} from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { alarmNotificationProfileTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode, adaptAlarmNotificationProfileData, addIdToAlarmDef
} from '../../utils/alarmNotificationProfileTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => AdaptedAlarmNotificationProfile
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedAlarmNotificationProfile: alarmNotificationProfileSelectors
    .getSelectedAlarmNotificationProfile(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedAlarmNotificationProfile: alarmNotificationProfileSelectors
    .getSelectedAlarmNotificationProfile(state)[0],
  alarmNotificationProfile: alarmNotificationProfileSelectors.getAlarmNotificationProfile(state),
  interceptAlarmList: alarmNotificationProfileSelectors.getInterceptAlarmList(state),
  alarmsInstList: alarmNotificationProfileSelectors.getAlarmsInstList(state),
  alarmsDefList: alarmNotificationProfileSelectors.getAlarmsDefList(state),
});

const mapDispatch = {
  goToAlarmNotificationProfileList: alarmNotificationProfileActions.goToAlarmNotificationProfileList,
  goToAlarmNotificationProfileEdit: alarmNotificationProfileActions.goToAlarmNotificationProfileEdit,
  getAlarmNotificationProfile: alarmNotificationProfileActions.getAlarmNotificationProfile,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AlarmNotificationProfileView = ({
  history,
  selectedAlarmNotificationProfile,
  deliveryFunctionId,
  alarmNotificationProfile,
  interceptAlarmList,
  alarmsInstList,
  alarmsDefList,
  goToAlarmNotificationProfileList,
  goToAlarmNotificationProfileEdit,
  getAlarmNotificationProfile,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AdaptedAlarmNotificationProfile> | null>(null);
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(alarmNotificationProfileTemplate, 'view'));
  const [currentContentType, setCurrentContentType] = useState<string>('');
  const [formGridData, setFormGridData] = useState<any[]>([]);
  const { t } = useTranslation();

  const id = selectedAlarmNotificationProfile?.alarmNtfyProfileId;
  useEffect(() => {
    if (id) {
      getAlarmNotificationProfile(String(id));
    }
  }, [
    id,
    getAlarmNotificationProfile,
  ]);

  useEffect(() => {
    // if the data not finish loading, the alarmNotificationProfile will still be the old one and have different id than the new selected one
    if (selectedAlarmNotificationProfile && selectedAlarmNotificationProfile?.alarmNtfyProfileId !== alarmNotificationProfile?.alarmNtfyProfileId) {
      return;
    }

    if (alarmNotificationProfile && template) {
      const adaptedAlarmNotificationProfile: AdaptedAlarmNotificationProfile
        = adaptAlarmNotificationProfileData(alarmNotificationProfile);
      setFormData(adaptedAlarmNotificationProfile);

      const contentType = adaptedAlarmNotificationProfile.profile_type;
      setCurrentContentType(contentType);
      if (contentType === 'ALARM_DEFINITION' && alarmsDefList?.length) {
        setFormGridData(addIdToAlarmDef(['alarmId'], alarmsDefList));
      } else if (contentType === 'ALARM_INSTANCE' && alarmsInstList?.length) {
        setFormGridData([]);
      } else if (contentType === 'INTERCEPT_ALARM' && interceptAlarmList?.length) {
        setFormGridData([]);
      }

      const clonedTemplate: FormTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [
    alarmNotificationProfile,
    alarmsDefList,
  ])

  const handleClickEdit = () => goToAlarmNotificationProfileEdit(history);

  const handleCancel = () => goToAlarmNotificationProfileList(history);

  const getInterceptAlarmListByParam = (body: any) : AxiosPromise<ServerResponse<InterceptAlarm>> => {
      return alarmNotificationApi.getInterceptAlarmList(body);
    }

  const getAlarmInstListByParam = (body: any): AxiosPromise<ServerResponse<AlarmsInst>> => {
      return alarmNotificationApi.getAlarmsInstList(body);
    }

  const renderForm = () => {
    if (formData && template && alarmsDefList) {
      return (
        <AlarmNotificationProfileForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing={false}
          gridData={formGridData}
          profileType={currentContentType}
          getAlarmIntercept={getInterceptAlarmListByParam}
          getAlarmInstace={getAlarmInstListByParam}
          alarmDefData={addIdToAlarmDef(['alarmId'], alarmsDefList)}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (formData == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="AlarmNotificationProfileView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('alarmNotificationProfile.view.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(AlarmNotificationProfileView));
